import { useState } from "react";
import { FLOWBOARD_LIST_PERSIST_KEY } from "./FlowboardList";

function readStorage(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(FLOWBOARD_LIST_PERSIST_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function patchStorage(patch: Record<string, unknown>) {
  try {
    localStorage.setItem(FLOWBOARD_LIST_PERSIST_KEY, JSON.stringify({ ...readStorage(), ...patch }));
  } catch { /* ignore quota errors */ }
}

export interface ColumnDef { id: string; header: unknown; defaultHidden?: boolean; }

export function useFlowboardColumnVisibility(columns: ColumnDef[]) {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => {
    const base: Record<string, boolean> = {};
    columns.forEach((c) => { base[c.id] = !c.defaultHidden; });
    const s = readStorage();
    return s.visible && typeof s.visible === "object"
      ? { ...base, ...(s.visible as Record<string, boolean>) }
      : base;
  });

  const toggleColumn = (id: string) => {
    setVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (Object.values(next).every((v) => !v)) return prev;
      patchStorage({ visible: next });
      return next;
    });
  };

  const resetColumns = () => {
    const base: Record<string, boolean> = {};
    columns.forEach((c) => { base[c.id] = !c.defaultHidden; });
    setVisibility(base);
    patchStorage({ visible: base });
  };

  const visibleCount = Object.values(visibility).filter(Boolean).length;

  return { visibility, toggleColumn, resetColumns, visibleCount };
}
